import { observer } from 'mobx-react-lite'
import { useState, useEffect } from 'react'
import { Autocomplete, createFilterOptions } from '@mui/material'
import { BoolProps } from '../../../../model/Boolean'
import { ModalWindow } from '../../../../component/common/analog/template/ModalWindow'
import { AppBox } from '../../../../component/common/analog/molecule/AppBox'
import { AppButton } from '../../../../component/common/analog/molecule/button/AppButton'
import { AppTextField } from '../../../form/component/analog/AppTextField'
import { AppDatePicker } from '../../../form/component/analog/AppDatePicker'
import { CreateCertificationDto } from '../../model/certification'
import { useCertificationStore } from '../../store/certification.store'
import { useLocalizationStore } from '../../../localization/store/localization.store'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'

export interface CertificationFormModalProps extends BoolProps {
  onSubmit: (data: CreateCertificationDto) => Promise<void>
}

export const CertificationFormModal = observer((props: CertificationFormModalProps) => {
  const certificationStore = useCertificationStore()
  const { translation } = useLocalizationStore()

  const [certificateName, setCertificateName] = useState('')
  const [issuer, setIssuer] = useState('')
  const [dateObtained, setDateObtained] = useState<Date | undefined>(new Date())
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined)

  // Create filter for autocomplete
  const filter = createFilterOptions<string>()

  // Load education and competencies if not already loaded
  useEffect(() => {
    if (!certificationStore.educationAndCompetencies) {
      certificationStore.handleGetEducationAndCompetencies()
    }
  }, [certificationStore])

  const educationAndCompetenciesOptions = certificationStore.educationAndCompetencies || []

  const resetForm = () => {
    setCertificateName('')
    setIssuer('')
    setDateObtained(new Date())
    setExpiryDate(undefined)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const dto: CreateCertificationDto = {
      certificateName,
      issuer: issuer || undefined,
      dateObtained: dateObtained?.toISOString().split('T')[0] || '',
      expiryDate: expiryDate?.toISOString().split('T')[0] || undefined,
    }
    
    await props.onSubmit(dto)
    resetForm()
    props.makeFalse()
  }

  const handleClose = () => {
    resetForm()
    props.makeFalse()
  }

  return (
    <ModalWindow
      modalProps={{
        open: props.isTrue,
        onClose: handleClose,
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '60%', lg: '40%' },
            borderRadius: '10px',
          },
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <AppBox sx={styles.form}>
          <Autocomplete
            sx={{
              width: '100%',
              '& .MuiInputBase-formControl': {
                backgroundColor: 'background.default',
                height: 40,
              },
              '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                color: 'text.primary',
              },
            }}
            size="small"
            value={certificateName}
            onChange={(_, newValue: string | null) => {
              setCertificateName(newValue || '')
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)
              const { inputValue } = params
              // Suggest the creation of a new value if not found
              const isExisting = options.some(option => inputValue === option)
            
              if (inputValue !== '' && !isExisting) {
                filtered.push(inputValue)
              }
            
              return filtered
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            freeSolo
            options={educationAndCompetenciesOptions}
            getOptionLabel={option => option}
            renderInput={params => (
              <AppTextField
                {...params}
                label={translation.pages.profile.certificationModal.certificateName.label()}
                placeholder={translation.pages.profile.certificationModal.certificateName.placeholder()}
                required
                fullWidth
                size="small"
                sx={{ margin: 0 }}
              />
            )}
          />
          <AppTextField
            label={translation.pages.profile.certificationModal.issuer.label()}
            placeholder={translation.pages.profile.certificationModal.issuer.placeholder()}
            value={issuer}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIssuer(e.target.value)}
            fullWidth
            size="small"
            sx={{ margin: 0 }}
          />
          <AppBox sx={styles.datePickerContainer}>
            <AppDatePicker
              id="dateObtained"
              label={translation.pages.profile.certificationModal.dateObtained.label()}
              defaultValue={dateObtained}
              onChange={setDateObtained}
            />
          </AppBox>
          <AppBox sx={styles.datePickerContainer}>
            <AppDatePicker
              id="expiryDate"
              label={translation.pages.profile.certificationModal.expiryDate.label()}
              noValueLabel={translation.pages.profile.certificationModal.expiryDate.noValueLabel()}
              defaultValue={expiryDate}
              onChange={setExpiryDate}
            />
          </AppBox>
          <AppDivider />
          <AppBox sx={styles.buttonContainer}>
            <AppButton
              fontSize={12}
              variant="light"
              textValue={translation.pages.profile.certificationModal.buttons.cancel()}
              onClick={handleClose}
            />
            <AppButton
              fontSize={12}
              variant="contained"
              textValue={translation.pages.profile.certificationModal.buttons.save()}
              type="submit"
              disabled={!certificateName.trim()}
            />
          </AppBox>
        </AppBox>
      </form>
    </ModalWindow>
  )
})

const styles = {
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  buttonContainer: {
    gap: 10,
    justifyContent: 'flex-end',
    width: '100%',
  },
  datePickerContainer: {
    width: '100%',
    '& .MuiFormControl-root': {
      marginBottom: 0,
    },
    '& .MuiTextField-root': {
      height: 40,
      '& .MuiInputBase-root': {
        height: 40,
        fontSize: 12,
      },
      '& .MuiInputLabel-root': {
        fontSize: 12,
      },
    },
  },
}